import * as React from 'react'
import { Menu, MenuMenu, Dropdown, DropdownMenu, DropdownItem, MenuItem } from 'semantic-ui-react'
import useCsrfFetchJson from './hooks/use_csrf_fetch'
import './mud.css'

const handleLogout = async () => {
  const csrfToken = document.querySelector('[name="csrf-token"]').content;

  const response = await fetch('/users/sign_out', {
    method: 'DELETE',
    headers: {
      'X-CSRF-Token': csrfToken,
      'Content-Type': 'application/json',
    },
    credentials: 'same-origin', // Ensures cookies are sent with the request
  });

  if (response.ok) {
    window.location.href = '/';  // Redirect to home or wherever you'd like after logging out
  }
};

const LoggedInMenu = ({
  user,
  player_portal_path,
  admin_portal_path,
  logout_path,
  can_see_admin
}) => {
  return (
    <Dropdown item text="Account">
      <DropdownMenu>
        <DropdownItem>
          <a href={player_portal_path}>Player Portal</a>
        </DropdownItem>
        {can_see_admin && <DropdownItem><a href={admin_portal_path}>Admin Portal</a></DropdownItem>}
        <DropdownItem onClick={handleLogout}>
          Log Out
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  )
};

const CharacterMenu = ({ user, new_character_path }) => {
  const fetchJson = useCsrfFetchJson();
  const [characters, setCharacters] = React.useState([]);
  const [currentCharacter, setCurrentCharacter] = React.useState(null);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);

  const loadCharacters = React.useCallback(async () => {
    try {
      setLoading(true);
      const [current, list] = await Promise.all([
        fetchJson('/player/characters/current'),
        fetchJson('/player/characters/mine')
      ]);
      setCurrentCharacter(current && current.id ? current : null);
      setCharacters(Array.isArray(list) ? list : []);
      setError(null);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }, [fetchJson]);

  React.useEffect(() => {
    if (!user) return;
    loadCharacters();
  }, [user, loadCharacters]);

  const handleSelect = async (characterId) => {
    try {
      const response = await fetchJson(`/player/characters/${characterId}/select`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ character_id: characterId })
      });
      if (response && response.current_character) {
        setCurrentCharacter(response.current_character);
      } else {
        loadCharacters();
      }
    } catch (err) {
      setError(err);
    }
  };

  if (!user) return null;

  const currentName = currentCharacter ? currentCharacter.name : 'Select Character';
  const currentId = currentCharacter ? currentCharacter.id : null;
  const characterPath = currentId ? `#characters/${currentId}` : null;

  return (
    <Dropdown item text={`${currentName}`}>
      <DropdownMenu>
        {loading && <DropdownItem disabled>Loading...</DropdownItem>}
        {error && <DropdownItem disabled>Error loading characters</DropdownItem>}
        {!loading && !error && (
          <>
            {characterPath ? (
              <>
                <DropdownItem as="a" href={characterPath}>Score</DropdownItem>
                {/* <DropdownItem as="a" href={characterPath}>Inventory</DropdownItem> */}
                {/* <DropdownItem as="a" href={characterPath}>Faction</DropdownItem> */}
              </>
            ) : (
              <>
                {/* <DropdownItem disabled>Score</DropdownItem> */}
                {/* <DropdownItem disabled>Inventory</DropdownItem> */}
                {/* <DropdownItem disabled>Faction</DropdownItem> */}
              </>
            )}
            <DropdownItem>
              <Dropdown text="Switch" pointing="left" className="link item">
                <DropdownMenu>
                  {characters.length === 0 && <DropdownItem disabled>No characters</DropdownItem>}
                  {characters.map((character) => (
                    <DropdownItem
                      key={character.id}
                      active={currentCharacter && character.id === currentCharacter.id}
                      onClick={() => handleSelect(character.id)}
                    >
                      {character.name}
                    </DropdownItem>
                  ))}
                </DropdownMenu>
              </Dropdown>
            </DropdownItem>
            <DropdownItem as="a" href={new_character_path}>Create Character</DropdownItem>
          </>
        )}
      </DropdownMenu>
    </Dropdown>
  );
};

const LoggedOutMenu = ({login_path}) => {
  return (
    <MenuItem className="lots-navbar__login">
      <a className="lots-btn lots-btn--ghost" href={login_path}>Log In</a>
    </MenuItem>
  );
}

const SocialMenu = () => {
  return (
    <Dropdown item text='Social'>
      <DropdownMenu>
        <DropdownItem>
          <a href='https://www.facebook.com/Legacy-of-the-Sith-LotS--323355740686/'>Facebook</a>
        </DropdownItem>
        <DropdownItem>
          <a href='https://discord.gg/rHq9qcc'>Discord</a>
        </DropdownItem>
      </DropdownMenu>
    </Dropdown>
  );
}

// Define the Navbar component
const Navbar = ({
  user,
  player_portal_path,
  admin_portal_path,
  how_to_play_path,
  new_character_path,
  logout_path,
  login_path,
  can_see_admin
}) => {
  // console.log('props', props);
  const logged_in_props = {
    user,
    player_portal_path,
    admin_portal_path,
    logout_path,
    can_see_admin
  };
  const logged_out_props = {
    login_path
  };

  return (
    <Menu className="lots-navbar" secondary>
      <MenuItem className="lots-navbar__brand">Legacy of the Sith</MenuItem>
      <SocialMenu />
      <MenuItem>
        <a href={how_to_play_path}>How to Play</a>
      </MenuItem>
      <MenuItem>
        <a href="/mud">Play Online (alpha)</a>
      </MenuItem>
      <MenuItem>
        <a href='https://ko-fi.com/legacyofthesith'>Donate</a>
      </MenuItem>
      <MenuMenu position='right'>
        {user && <CharacterMenu user={user} new_character_path={new_character_path} />}
        {user ? <LoggedInMenu {...logged_in_props} /> : <LoggedOutMenu {...logged_out_props} />}
      </MenuMenu>
    </Menu>
  )
};

export default Navbar;
